import React, { useEffect, useState } from "react";
import { cqAPI } from "../../lib/Auth";
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  Checkbox,
  Tooltip,
  useTheme,
  useColorMode,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  Spinner,
  ModalCloseButton,
  ModalBody,
  IconButton,
  ModalFooter,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  Menu,
  MenuItem,
  MenuButton,
  useToast,
  MenuList,
  Divider,
} from "@chakra-ui/react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisH } from "@fortawesome/free-solid-svg-icons";
import { AddIcon } from "@chakra-ui/icons";
import EditableText from "../EditableText/EditableText";
import dialogManager from "../DialogManager/DialogManager";
import { useParams } from "react-router-dom";

const [emailExp, nameExp] = [
  /^[^ @\>\)\(\<]{1,}@[^.@ ]{1,}.[^@ \>\<\)\(]+/,
  /[a-zA-Z]+\ [a-zA-Z]+/,
];

const HEADER_NOTICE = "Notice";
const HEADER_ERROR = "Error";

function PartnerUsers() {
  const theme = useTheme();
  const { colorMode } = useColorMode();
  const toast = useToast();
  const { partner_id } = useParams();

  const [userData, setUserData] = useState({ users: [] });
  const {
    isOpen: isNewUserOpen,
    onOpen: onNewUserOpen,
    onClose,
  } = useDisclosure();
  const [newUser, setNewUser] = useState({ name: "", email: "" });

  const onNewUserClose = () => {
    setNewUser({ name: "", email: "" });
    onClose();
  };

  const fetchUserData = async () => {
    const response = await cqAPI.get(`/settings/partner/users?partner_id=${partner_id}`);
    if (response?.type === "ok") {
      setUserData({ users: response.data.users || [] });
    } else {
      toast({
        title: "Error",
        description: "Failed to load users.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    fetchUserData();
  }, [partner_id]);

  const hoverBgColor =
    colorMode === "light" ? theme.colors.gray[200] : theme.colors.gray[700];
  const dividerColor = colorMode === "light" ? "gray.200" : "gray.700";
  const textColor = colorMode === "light" ? theme.colors.lightText : theme.colors.darkText;

  const handleSaveNewUser = async () => {
    if (!nameExp.test(newUser.name)) {
      toast({
        title: "Invalid Name",
        description: "Please enter a valid full name: First Last",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    if (!emailExp.test(newUser.email)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const response = await cqAPI.post(`/settings/partner/users?partner_id=${partner_id}`, {
      email: newUser.email,
      name: newUser.name,
    });

    if (response.msg) {
      dialogManager.open({
        onDeny: () => {},
        acceptText: "OK",
        body: response.msg,
        header: HEADER_NOTICE,
      });
    }
    if (response.type === "ok") {
      onNewUserClose();
      await fetchUserData();
    }
  };

  const updateUserName = async (uid, name) => {
    const response = await cqAPI.post(`/settings/partner/users?partner_id=${partner_id}`, {
      name: name,
      pid: uid,
    });
    if (response.msg) {
      dialogManager.open({
        onDeny: () => {},
        acceptText: "OK",
        body: response.msg,
        header: HEADER_ERROR,
      });
    }
    return response.type === "ok";
  };

  const resendInvitation = async (uid, email) => {
    const response = await cqAPI.post(`/settings/users/resend`, {
      pid: uid,
      ...(email ? { email } : {}),
    });
    if (response.msg) {
      dialogManager.open({
        onDeny: () => {},
        acceptText: "OK",
        body: response.msg,
        header: HEADER_NOTICE,
      });
    }
    if (response.type === "ok") {
      toast({
        title: "Success",
        description: "The invitation was resent.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      return true;
    }
    return false;
  };

  const deleteAllAccess = async (uid) => {
    const response = await cqAPI.del(`/settings/partner/users?partner_id=${partner_id}`, {
      pid: uid,
    });
    if (response.msg) {
      dialogManager.open({
        onDeny: () => {},
        acceptText: "OK",
        body: response.msg,
        header: HEADER_ERROR,
      });
    }
    if (response.type === "ok") {
      setUserData({
        ...userData,
        users: userData.users.filter((user) => user._id !== uid),
      });
    }
  };

  const NewUserModal = (
    <Modal isOpen={isNewUserOpen} onClose={onNewUserClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader textAlign="left">Invite a new user</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel textAlign="left">Full Name</FormLabel>
              <Input
                placeholder="Full Name"
                value={newUser.name}
                onChange={(e) =>
                  setNewUser({ ...newUser, name: e.target.value })
                }
              />
            </FormControl>
            <FormControl>
              <FormLabel textAlign="left">Email</FormLabel>
              <Input
                placeholder="Email"
                value={newUser.email}
                onChange={(e) =>
                  setNewUser({ ...newUser, email: e.target.value })
                }
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="blue" mr={3} onClick={handleSaveNewUser}>
            Save
          </Button>
          <Button variant="ghost" onClick={onNewUserClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  return (
    <Box p={4} overflowY="auto" height="100%" width="100%">
      {NewUserModal}
      <Flex justifyContent="flex-end" mb={4}>
        <Button leftIcon={<AddIcon />} onClick={onNewUserOpen}>
          Add User
        </Button>
      </Flex>
      <VStack spacing={4} align="stretch">
        <Flex borderBottom="1px" borderColor={dividerColor} p={2}>
          <HStack spacing={8} flex="1">
            <Text fontSize="sm" flexBasis="30%" fontWeight="bold">
              Info
            </Text>
            <Text fontSize="sm" flexBasis="50%" fontWeight="bold">
              Email
            </Text>
            <Text fontSize="sm" flexBasis="20%" fontWeight="bold">
              Action
            </Text>
          </HStack>
        </Flex>
        {userData.users.map((user) => (
          <Flex
            key={user._id}
            borderBottom="1px" borderColor={dividerColor}
            p={2}
            _hover={{ bg: hoverBgColor, cursor: "pointer" }}
          >
            <HStack spacing={8} flex="1">
              <Flex justifyContent="left" flexBasis="30%">
                <EditableText
                  initialValue={user.profile.name}
                  onSave={(newValue) => updateUserName(user._id, newValue)}
                  placeholder="Name"
                  pattern={nameExp}
                  invalid="Enter a valid full name: First Last"
                />
              </Flex>
              <Flex flexBasis="50%">
                <EditableText
                  initialValue={user.email}
                  editable={!user.verified}
                  onSave={(newValue) => resendInvitation(user._id, newValue)}
                  placeholder="Email"
                  pattern={emailExp}
                  invalid="Please enter a valid email address."
                />
              </Flex>
              <Menu flexBasis="20%">
                <MenuButton
                  as={IconButton}
                  icon={<FontAwesomeIcon icon={faEllipsisH} />}
                  backgroundColor={hoverBgColor}
                  color={textColor}
                />
                <MenuList>
                  {user.verified && (
                    <MenuItem onClick={() => deleteAllAccess(user._id)}>
                      Remove All Access
                    </MenuItem>
                  )}
                  {!user.verified && (
                    <>
                      <MenuItem onClick={() => deleteAllAccess(user._id)}>
                        Rescind Invitation
                      </MenuItem>
                      <MenuItem onClick={() => resendInvitation(user._id)}>
                        Resend Invitation
                      </MenuItem>
                    </>
                  )}
                </MenuList>
              </Menu>
            </HStack>
          </Flex>
        ))}
      </VStack>
    </Box>
  );
}

export default PartnerUsers;
